<script>
  import { createEventDispatcher, onDestroy, onMount } from 'svelte';
  import Modal from '@components/Modal.svelte';
  import Button from '@components/Button.svelte';
  import PreloadBox from '@components/PreloadBox.svelte';
  import Alert from '@components/Alert.svelte';
  import Notification from '@utils/notification';
  import Track from '@utils/track';
  import { getCurrentUserStore } from '@stores/current_user';
  import ImageUpload from '@utils/image-upload';

  import TileCoverPhotos from '@app-components/profile/TileCoverPhotos.svelte';

  import GroupAjax from '@ajax/group';

  const dispatch = createEventDispatcher();

  export let mobile = false;
  export let showModal = false;
  export let group;

  const maxSelected = 3;

  let currentUser = getCurrentUserStore('default');
  let unsub = group.subscribe((x) => (group = x));

  let loadingPossiblePhotos = false;
  let originalCoverPhotos = [];
  let coverPhotos;
  let possibleCoverPhotos;

  let fileInput;
  let lastSelectIndex;
  let showCancelChangesDialog = false;
  let userChangedInput = false;

  let close = function () {
    showModal = false;
    dispatch('close');
  };

  let uploadImage = function () {
    fileInput.click();
  };

  let attachImageUpload = function (node) {
    ImageUpload.attach(
      node,
      function (data) {
        if (data && data.length > 0 && data[0] && data[0].uploadURL) {
          let url = data[0].uploadURL;
          possibleCoverPhotos = [{ url: url, source: 'upload' }, ...possibleCoverPhotos];

          if (coverPhotos && coverPhotos.length < maxSelected) {
            coverPhotos = [...coverPhotos, { url: url, source: 'upload' }];
          }

          userChangedInput = true;
        }
      },
      (_percentage, _file, _progress) => {},
      {
        maxNumberOfFiles: 1,
        cropper: {
          edit: (file) => {
            return new Promise((resolve, _reject) => {
              resolve({ file: file });
            });
          },
        },
      }
    );
  };

  let listToMatrix = function (list, elementsPerSubArray) {
    let matrix = [],
      i,
      k;

    for (i = 0, k = -1; i < list.length; i++) {
      if (i % elementsPerSubArray === 0) {
        k++;
        matrix[k] = [];
      }

      matrix[k].push(list[i]);
    }

    return matrix;
  };

  let unselectImage = function (src) {
    let idx = coverPhotoUrls().indexOf(src.url);
    coverPhotos.splice(idx, 1);
    coverPhotos = coverPhotos;
  };

  let selectImage = function (src) {
    if (coverPhotos.length >= maxSelected) {
      coverPhotos[(lastSelectIndex + 1) % maxSelected] = src;
    } else {
      coverPhotos.push(src);
    }

    coverPhotos = coverPhotos;
    lastSelectIndex = (lastSelectIndex + 1) % maxSelected;
  };

  let setPhotoDisplayType = function (index) {
    let currentDisplayType = coverPhotos[index].display_style || 'cover';
    let newDisplayType = currentDisplayType == 'cover' ? 'contain' : 'cover';
    coverPhotos[index] = {
      url: coverPhotos[index].url,
      display_style: newDisplayType,
      source: coverPhotos[index]?.source,
    };
    coverPhotos = [...coverPhotos];
  };

  let toggleImage = function (photo) {
    if (coverPhotoUrls().includes(photo.url)) {
      unselectImage(photo);
    } else {
      selectImage(photo);
    }
    userChangedInput = true;
  };

  let saveCoverPhotos = function () {
    let data = {
      cover_photos: coverPhotos,
    };

    GroupAjax.updateJSON(group, data);
    group.set({ ...group, ...data, minified_cover_image: null });

    Track.groupCoverPhotoChange({
      user: currentUser,
      group,
    });

    close();
    Notification.send({ text: 'Cover photos have been updated' });
  };

  let loadPossibleCoverPhotos = async function () {
    loadingPossiblePhotos = true;
    possibleCoverPhotos = await GroupAjax.possibleCoverPhotos(group);
    loadingPossiblePhotos = false;
  };

  $: coverPhotoUrls = function () {
    return coverPhotos.map((elem) => elem.url);
  };

  loadPossibleCoverPhotos();

  let closeBtnClicked = function () {
    if (userChangedInput) {
      showCancelChangesDialog = true;
    } else {
      close();
    }
  };

  onDestroy(() => {
    unsub();
  });

  onMount(() => {
    originalCoverPhotos = group && group.cover_photos ? [...group.cover_photos] : [];
    coverPhotos = originalCoverPhotos;
    lastSelectIndex = coverPhotos.length - 1;
  });
</script>

<style style="text/scss" lang="scss">
  .cover-photos-header {
    margin-top: -55px;

    .cover-photos-title {
      font-weight: 500;
      font-size: 20px;
      line-height: 28px;
      text-align: center;
    }

    .cover-photos-subtitle {
      margin-top: 8px;
      font-size: 14px;
      line-height: 20px;
      opacity: 0.5;
      text-align: center;
    }
  }

  .left-right-container {
    display: flex;

    .left-container {
      margin-top: 12px;
      width: 400px;
      flex-shrink: 0;

      .preview-container {
        background-image: url(/images/change-cover-photos/desktop-bg.png);
        height: 466px;
        width: 400px;
        position: relative;

        .preview-images {
          position: absolute;
          top: 100px;
          left: 70px;
          width: 256px;
          height: 200px;
        }
      }
    }

    .right-container {
      display: flex;
      flex-direction: column;
      flex: 1;
      margin-top: 40px;
      margin-left: 10px;
      max-height: 65vh;
      overflow-y: auto;
      overflow-x: hidden;
      font-size: 14px;
      font-weight: 500;

      .upload-btn {
        position: absolute;
        right: 35px;
        top: 90px;
        color: #a893f9;
        cursor: pointer;

        &:hover {
          color: #7054dc;
        }
      }
    }
  }

  .img-row {
    display: flex;
    margin-bottom: 7px;

    .single-image {
      flex: 1;
      cursor: pointer;
      position: relative;
      margin-left: 3px;
      margin-right: 3px;
      border-radius: 8px;
      border: 3px solid white;
      max-width: 155px;
      max-height: 177px;

      &.selected-image {
        border: 3px solid #a893f9;
      }

      .purple-box-top-left {
        position: absolute;
        top: 0px;
        right: 0px;
        width: 24px;
        height: 24px;
        background: #a893f9;
        color: white;
        font-size: 13px;
        font-weight: 500;
        border-radius: 0px 0px 0px 4px;
        display: flex;
        align-items: center;
        justify-content: center;

        img {
          width: 14px;
          height: 14px;
          max-height: unset;
          overflow: unset;
          object-fit: unset;
          border-radius: unset;
        }
      }

      img {
        width: 100%;
        height: 100%;
        overflow: hidden;
        object-fit: cover;
        border-radius: 5px;
      }
    }
  }

  .bottom-container {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-left: -30px;
    margin-right: -30px;
    margin-top: 30px;
    margin-bottom: -50px;
    height: 80px;
    box-shadow: 0px 22px 100px rgba(0, 0, 0, 0.0938593);
  }

  * :global(.change-cover-mobile-bottom-bar) {
    position: fixed;
    bottom: 0px;
    z-index: 99;
    background: white;
    box-shadow: 0px 22px 100px rgba(0, 0, 0, 0.0938593);
    left: 0px;
    right: 0px;
    padding-left: 20px;
    padding-right: 20px;
  }

  .mobile {
    .mobile-container {
      margin-top: 30px;
      margin-bottom: 60px;

      .image-container {
        background: #ffffff;
        box-shadow: 0px 8px 40px rgba(0, 0, 0, 0.1);
        border-radius: 15px;
        padding: 30px;
      }

      .selector-container {
        margin-top: 24px;
        display: flex;
        flex-direction: column;

        .upload-btn {
          font-weight: 500;
          font-size: 14px;
          line-height: 17px;
          color: #896bd9;
          display: block;
          text-align: right;
          width: 100%;
        }

        .selector-images {
          display: flex;
          margin-top: 16px;
          flex-direction: column;

          .img-row {
            width: 100%;

            .single-image {
              max-width: 33%;
            }
          }
        }
      }
    }
  }
</style>

<div bind:this={fileInput} style="display: none" use:attachImageUpload />

<div class="svelte-modal">
  <Alert
    mobile={mobile}
    on:yes-click={() => {
      close();
    }}
    question="Exit without saving?"
    yesButtonText="Exit"
    objectText={null}
    bind:show={showCancelChangesDialog} />

  <Modal
    closeOnOverlayClick={false}
    on:overlay-clicked={closeBtnClicked}
    mobile={mobile}
    hide={closeBtnClicked}
    show={showModal}
    style={mobile ? '' : 'min-width: 952px;'}>
    <svelte:fragment slot="content-before">
      {#if mobile}
        <div class="change-cover-mobile-bottom-bar">
          <Button on:click={saveCoverPhotos}>Confirm</Button>
        </div>
      {/if}
    </svelte:fragment>

    <div slot="content" class:mobile={mobile}>
      <div class="cover-photos-header">
        <div class="cover-photos-title">Change cover photos</div>
        <div class="cover-photos-subtitle">
          Select up to {maxSelected} photos
        </div>
      </div>

      {#if mobile}
        <div class="mobile-container">
          <div class="image-container">
            <TileCoverPhotos
              editable={true}
              setPhotoDisplayType={setPhotoDisplayType}
              group={{ ...group, cover_photos: coverPhotos }} />
          </div>
          <div class="selector-container">
            <div class="upload-btn" on:click={uploadImage}>Upload</div>
            <div class="selector-images">
              {#if loadingPossiblePhotos}
                <div class="img-row">
                  <div class="single-image" style="width: 33%; height: 120px;">
                    <PreloadBox />
                  </div>
                  <div class="single-image" style="width: 33%; height: 120px;">
                    <PreloadBox />
                  </div>
                  <div class="single-image" style="width: 33%; height: 120px;">
                    <PreloadBox />
                  </div>
                </div>
              {:else}
                {#each listToMatrix(possibleCoverPhotos, maxSelected) as row}
                  <div class="img-row">
                    {#each row as cp}
                      <div
                        class="single-image"
                        class:selected-image={coverPhotoUrls().includes(cp.url)}
                        on:click={() => toggleImage(cp)}>
                        {#if coverPhotoUrls().includes(cp.url)}
                          <div class="purple-box-top-left">
                            {coverPhotoUrls().indexOf(cp.url) + 1}
                          </div>
                        {/if}
                        <img src={cp.url} />
                      </div>
                    {/each}
                  </div>
                {/each}
              {/if}
            </div>
          </div>
        </div>
      {:else}
        <div class="left-right-container">
          <div class="left-container">
            <div class="preview-container">
              <div class="preview-images">
                <TileCoverPhotos
                  editable={true}
                  setPhotoDisplayType={setPhotoDisplayType}
                  group={{ ...group, cover_photos: coverPhotos }} />
              </div>
            </div>
          </div>
          <div class="right-container purple-scrollbar">
            {#if loadingPossiblePhotos}
              <div class="img-row">
                <div class="single-image" style="height: 140px; max-width: 140px;">
                  <PreloadBox />
                </div>
                <div class="single-image" style="height: 140px; max-width: 140px;">
                  <PreloadBox />
                </div>
                <div class="single-image" style="height: 140px; max-width: 140px;">
                  <PreloadBox />
                </div>
              </div>
              <div class="img-row">
                <div class="single-image" style="height: 140px; max-width: 140px;">
                  <PreloadBox />
                </div>
                <div class="single-image" style="height: 140px; max-width: 140px;">
                  <PreloadBox />
                </div>
              </div>
            {:else}
              <div class="upload-btn" on:click={uploadImage}>Upload</div>

              {#each listToMatrix(possibleCoverPhotos, maxSelected) as row}
                <div class="img-row">
                  {#each row as cp}
                    <div
                      class="single-image"
                      class:selected-image={coverPhotoUrls().includes(cp.url)}
                      on:click={() => toggleImage(cp)}>
                      {#if coverPhotoUrls().includes(cp.url)}
                        <div class="purple-box-top-left">
                          {coverPhotoUrls().indexOf(cp.url) + 1}
                        </div>
                      {/if}
                      <img src={cp.url} />
                    </div>
                  {/each}
                </div>
              {/each}
            {/if}
          </div>
        </div>

        <div class="bottom-container">
          <Button style="width: 100px; line-height: 0px;" on:click={saveCoverPhotos}>Confirm</Button>
        </div>
      {/if}
    </div>
  </Modal>
</div>
